import { useQuery } from '@apollo/client';
import { AddBlock, Companies } from '@clearkit/icons';
import classNames from 'classnames';
import { CKCheckboxNext, CKCountUp, CKLayoutGrid } from 'clearkit';
import { useEffect, useState } from 'react';

import { routes } from '~/components/app/appMainRoutes';
import BulkSelectedCompaniesActions from '~/components/tools/BulkSelectedCompaniesActions';
import { PageLayout } from '~/components/unified/PageLayout';
import { trackEvent } from '~/lib/tracking';

import { Search } from '../hooks/useTargetMarket';
import TargetMarketCompaniesQuery from '../queries/TargetMarketCompanies.graphql';
import { SAMPLE_COMPANIES } from '../Sidebar/logic';
import { TargetMarketsCompany } from '../types';
import CompanyRow from './CompanyRow';

const PLACEHOLDER_COMPANY: TargetMarketsCompany = {
  id: '',
  domain: '',
  name: 'Placeholder',
  country: 'Placeholder',
  industry: 'Placeholder',
  employeesRange: 'Placeholder',
  linkedin: 'Placeholder',
  twitter: 'Placeholder',
  crunchbase: 'Placeholder',
};

const CompaniesList = ({ search }: { search: Search | null }) => {
  const [previousTotalCompanies, setPreviousTotalCompanies] = useState(0);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const { data } = useQuery(TargetMarketCompaniesQuery, {
    skip: !search,
    variables: {
      country: search?.country || [],
      description: search?.description || [],
      employees: search?.employees || [],
      industry: search?.industry || [],
      raised: search?.raised || [],
      tag: search?.tag || [],
      tech: search?.tech || [],
      type: search?.type || [],
    },
  }) as {
    data?: {
      targetMarketCompanies?: {
        total: number;
        companies: TargetMarketsCompany[];
      };
    };
  };
  const showPlaceholder = search === null;

  useEffect(() => {
    if (data) {
      setPreviousTotalCompanies(data?.targetMarketCompanies?.total || 0);
    }
  }, [data]);

  const loading = data === undefined;
  const total = data?.targetMarketCompanies?.total || 0;
  const companies: TargetMarketsCompany[] = showPlaceholder
    ? SAMPLE_COMPANIES
    : loading
    ? Array(20).fill(PLACEHOLDER_COMPANY)
    : data?.targetMarketCompanies?.companies || [];

  return (
    <PageLayout>
      <PageLayout.Body
        className={classNames('relative', {
          'overflow-hidden': showPlaceholder,
        })}
      >
        {showPlaceholder && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-gray-300 bg-opacity-25 backdrop-blur-sm">
            <div className="items-center max-w-sm p-8 text-center ck-box">
              <h3 className="mb-2 font-semibold">Target Markets</h3>

              <p>
                Please create a new list or select an existing one to see
                matching companies.
              </p>

              <a
                className="mt-2 ck-button ck-button--simple ck-button--blue"
                href={`${routes.targetMarkets}?new=true`}
                onClick={() => trackEvent('target_market_new_list_clicked')}
              >
                <AddBlock className="mr-1" />
                <span>New list</span>
              </a>
            </div>
          </div>
        )}

        <div className="sticky top-0 z-30 px-6 py-2 text-gray-600 border-b bg-gray-0 xl:px-10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center text-sm gap-1">
              <Companies className="mr-1" height={24} width={24} />
              {showPlaceholder ? (
                '100M+ companies'
              ) : (
                <>
                  <CKCountUp
                    duration={1}
                    end={total}
                    start={previousTotalCompanies}
                  />{' '}
                  companies
                </>
              )}
            </div>

            {/* <CKTooltip tooltip="Export to CSV is coming soon.">
              <CKButton isDisabled onClick={() => {}} size="small">
                Export to CSV
              </CKButton>
            </CKTooltip> */}
          </div>

          <div className="relative items-center hidden text-sm font-semibold text-left lg:grid gap-2 grid-cols-[repeat(17,_minmax(0,_1fr))]">
            <div className="flex items-center col-span-1 gap-2">
              <CKCheckboxNext
                checked={
                  !!companies.length &&
                  companies.length === selectedCompanies.length
                }
                className="text-sm text-gray-600"
                isDisabled={loading || companies.length === 0}
                isIndeterminate={
                  selectedCompanies.length > 0 &&
                  selectedCompanies.length !== companies.length
                }
                // need to add a key to force re-render
                // as otherwise it's not reflecting changes
                key={`checkbox${companies.length.toString()}${selectedCompanies.length.toString()}`}
                onChange={(checked) => {
                  if (checked) {
                    const domains = companies.map((company) => company.domain);

                    setSelectedCompanies(companies ? domains : []);

                    trackEvent('target_market_all_companies_selected', {
                      domains,
                    });
                  } else {
                    setSelectedCompanies([]);

                    trackEvent('target_market_selected_companies_cleared');
                  }
                }}
              >
                #
              </CKCheckboxNext>
            </div>

            <div className="col-span-5">Company</div>

            <div className="col-span-3">Country</div>

            <div className="col-span-3">Industry</div>

            <div className="col-span-3">Employees</div>

            <div className="col-span-2"></div>
          </div>
        </div>

        {!loading && companies.length === 0 ? (
          <div className="px-6 py-40 text-center bg-white">
            <p className="text-center">
              No companies found, please try
              <br />
              updating your filters.
            </p>
          </div>
        ) : (
          <CKLayoutGrid className="w-full" gap="0">
            {companies.map((company, index) => (
              <CompanyRow
                data={company}
                handleSelect={(domain, selectCompany) => {
                  if (selectCompany) {
                    setSelectedCompanies((prev) => [...prev, domain]);

                    trackEvent('target_market_company_selected', { domain });
                  } else {
                    setSelectedCompanies((prev) =>
                      prev.filter((item) => item !== domain),
                    );

                    trackEvent('target_market_company_deselected', { domain });
                  }
                }}
                isSelected={selectedCompanies.includes(company.domain)}
                key={
                  (selectedCompanies.includes(company.domain)
                    ? 'checked'
                    : 'unchecked') +
                  company.domain.toString() +
                  index.toString()
                }
                loading={showPlaceholder ? false : loading}
                number={index + 1}
              />
            ))}
          </CKLayoutGrid>
        )}

        <BulkSelectedCompaniesActions
          handleClear={() => {
            setSelectedCompanies([]);

            trackEvent('target_market_selected_companies_cleared');
          }}
          isSample={false}
          selected={selectedCompanies}
          type="target_markets"
        />
      </PageLayout.Body>
    </PageLayout>
  );
};

export default CompaniesList;
